import { FormInstance } from 'antd'
import { SagaIterator } from 'redux-saga'
import { call, fork, put, takeLatest } from 'redux-saga/effects'
import { generateFullName } from '../../containers/PatientsView/PatientsViewStyle'
import { handleGetPatientsAdmissionData } from '../patientAdmission/saga'
import {
  ACTIVATE_PATIENT,
  ADD_PATIENT,
  ADD_PATIENT_WITH_PATIENT_KEY,
  DELETE_PATIENT,
  GET_PATIENT,
  GET_PATIENTS,
  SET_UPDATE_PATIENT,
  SET_UPDATE_PATIENT_THRESHOLD,
  UPDATE_PATIENT,
  UPDATE_PATIENT_THRESHOLD,
} from './actionTypes'
import {
  activatePatientData,
  addPatientData,
  deletePatientData,
  getPatientData,
  getPatientThresholdData,
  getPatientsData,
  updatePatientData,
  updatePatientThresholdData,
} from './api'
import {
  addPatientSuccess,
  addPatients,
  deletePatientSuccess,
  errorPatient,
  getPatients,
  setPatientForUpdateSuccess,
  setPatientThresholdForUpdateSuccess,
  successPatient,
  updatePatientSuccess,
  updatePatientThresholdSuccess,
} from './patientReducer'
import {
  AssignedDoctorNameType,
  GetPatientDataType,
  Patient,
  PatientsType,
} from './types'

type Action = {
  type: string
  payload: string
}

type postAction = {
  type: string
  payload: Patient
}

type updatePatientAction = {
  type: string
  payload: {
    key: string
    form: FormInstance<any>
    dob: string
  }
}

export function* handleSetUpdatePatientData(
  actions: updatePatientAction
): SagaIterator<void> {
  const response = yield call(getPatientData, actions.payload.key)
  const { data } = response
  const unmodifiedData: GetPatientDataType = data.patient
  let newPatientData: Patient = {
    patientKey: unmodifiedData.patientKey,
    patientId: unmodifiedData.patientId,
    bed_ward: `${unmodifiedData.bed ?? ' -'} | ${unmodifiedData.ward ?? ' -'}`,
    dob: unmodifiedData.dob,
    title: unmodifiedData.title,
    firstName: unmodifiedData.firstName,
    lastName: unmodifiedData.lastName,
    status: unmodifiedData.status,
    emergencyPhone: unmodifiedData.contactPersonPhone1,
    emergencyPhone2: unmodifiedData.contactPersonPhone2,
    emergencyContact: unmodifiedData.contactPersonName,
    emailId: unmodifiedData.contactPersonEmail,
    sex: unmodifiedData.gender,
    admissionDate: unmodifiedData.dateOfAdmission,
    age: unmodifiedData.age,
    assignedDoctor: '',
  }
  yield put(
    setPatientForUpdateSuccess({
      form: actions.payload.form,
      patientData: newPatientData,
    })
  )
}

export function* handleGetPatientData(actions: Action): SagaIterator<void> {
  try {
    const response = yield call(getPatientData, actions.payload)
    const { data } = response
    const unmodifiedData: GetPatientDataType = data.patient
    let newPatientData: Patient = {
      patientKey: unmodifiedData.patientKey,
      patientId: unmodifiedData.patientId,
      bed_ward: `${unmodifiedData.bed ?? ' -'} | ${unmodifiedData.ward ?? ' -'}`,
      dob: '',
      title: unmodifiedData.title,
      firstName: unmodifiedData.firstName,
      lastName: unmodifiedData.lastName,
      status: unmodifiedData.status,
      emergencyPhone: unmodifiedData.contactPersonPhone1,
      emergencyPhone2: unmodifiedData.contactPersonPhone2,
      emergencyContact: unmodifiedData.contactPersonName,
      emailId: unmodifiedData.contactPersonEmail,
      sex: unmodifiedData.gender,
      admissionDate: unmodifiedData.dateOfAdmission,
      age: unmodifiedData.age,
      assignedDoctor: '',
    }
    yield put(addPatientSuccess(newPatientData))
    yield put(getPatients())
  } catch (error: any) {
    yield put(errorPatient(error.response.data.message))
  }
}

export function* handleGetPatientsData(): SagaIterator<void> {
  try {
    const response = yield call(getPatientsData)
    const { data } = response
    let unmodifiedData = data.patients
    let newData: Patient[] = unmodifiedData.map((data: PatientsType) => {
      return {
        patientId: data.patient.id,
        patientKey: data.patient.key,
        dob: data.patient.dob,
        title: data.patient.name.title,
        firstName: data.patient.name.firstName,
        lastName: data.patient.name.lastName,
        emergencyPhone: data.emergencyContact.phone,
        emergencyPhone2: data.emergencyContact.phone2,
        emergencyContact: data.emergencyContact.name,
        sex: data.patient.sex,
        status: data.patient.status,
        admissionDate: data.doa,
        bed_ward: `${data.bed ?? ' -'} | ${data.ward ?? ' -'}`,
        age: data.patient.age,
        assignedDoctor: getAssignedDoctor(data.assignedDoctorName),
      }
    })
    yield put(addPatients(newData))
  } catch (error: any) {
    yield put(errorPatient(error.response.data.message))
  }
}

function getAssignedDoctor(data: AssignedDoctorNameType) {
  if (data.firstName && data.lastName) {
    return generateFullName(data.title, data.firstName, data.lastName)
  }
  return 'No Doctors Assigned'
}

export function* handleDeletePatientData(actions: Action): SagaIterator<void> {
  try {
    const response = yield call(deletePatientData, actions.payload)
    yield put(deletePatientSuccess(actions.payload))
    yield put(getPatients())
    const { data } = response
    yield put(successPatient(data.status))
  } catch (error: any) {
    yield put(errorPatient(error.response.data.message))
  }
}

export function* handleActivatePatientData(
  actions: Action
): SagaIterator<void> {
  try {
    const response = yield call(activatePatientData, actions.payload)
    yield put(getPatients())
    const { data } = response
    yield put(successPatient(data.status))
  } catch (error: any) {
    yield put(errorPatient(error.response.data.message))
  }
}

export function* handleAddPatientData(actions: postAction): SagaIterator<void> {
  try {
    const response = yield call(addPatientData, actions.payload)
    const { data } = response
    const newPatientData = {
      ...actions.payload,
      patientKey: data.patientKey,
    }
    yield put(addPatientSuccess(newPatientData))
    yield put(getPatients())
    yield put(successPatient(data.status))
  } catch (error: any) {
    yield put(errorPatient(error.response.data.message))
  }
}

export function* handleUpdatePatientData(
  actions: postAction
): SagaIterator<void> {
  try {
    const response = yield call(
      updatePatientData,
      actions.payload.patientKey,
      actions.payload
    )
    const { data } = response
    yield put(updatePatientSuccess([actions.payload]))
    yield put(getPatients())
    yield put(successPatient(data.status))
    yield call(handleGetPatientsAdmissionData)
  } catch (error: any) {
    yield put(errorPatient(error.response.data.message))
  }
}

type updatePatientThresholdAction = {
  type: string
  payload: {
    key: string
    form: FormInstance<any>
  }
}

export function* handleSetUpdatePatientThresholdData(
  actions: updatePatientThresholdAction
): SagaIterator<void> {
  const response = yield call(getPatientThresholdData, actions.payload.key)
  const { data } = response
  const unmodifiedData: object = data.patientThreshold

  yield put(
    setPatientThresholdForUpdateSuccess({
      form: actions.payload.form,
      thresholdData: unmodifiedData,
    })
  )
}

type postThresholdAction = {
  type: string
  payload: {
    key: string
    thresholdData: object
  }
}

export function* handleUpdatePatientThresholdData(
  actions: postThresholdAction
): SagaIterator<void> {
  try {
    const response = yield call(
      updatePatientThresholdData,
      actions.payload.key,
      actions.payload.thresholdData
    )
    yield put(updatePatientThresholdSuccess(actions.payload.thresholdData))
    const { data } = response
    yield put(successPatient(data.status))
  } catch (error: any) {
    yield put(errorPatient(error.response.data.message))
  }
}

type postPatientWithAlertsConfiguration = {
  type: string
  payload: {
    patient: Patient
    setPatientKey: (value: string) => void
  }
}

export function* handlePatientDataWithPatientKey(
  actions: postPatientWithAlertsConfiguration
): SagaIterator<void> {
  try {
    const response = yield call(addPatientData, actions.payload.patient)
    const { data } = response
    const newPatientData = {
      ...actions.payload.patient,
      patientKey: data.patientKey,
    }
    actions.payload.setPatientKey(data.patientKey)
    yield put(addPatientSuccess(newPatientData))
    yield put(getPatients())
    yield put(successPatient(data.status))
  } catch (error: any) {
    yield put(errorPatient(error.response.data.message))
  }
}

export function* watcherSaga() {
  yield takeLatest(GET_PATIENT, handleGetPatientData)
  yield takeLatest(GET_PATIENTS, handleGetPatientsData)
  yield takeLatest(ADD_PATIENT, handleAddPatientData)
  yield takeLatest(DELETE_PATIENT, handleDeletePatientData)
  yield takeLatest(UPDATE_PATIENT, handleUpdatePatientData)
  yield takeLatest(ACTIVATE_PATIENT, handleActivatePatientData)
  yield takeLatest(SET_UPDATE_PATIENT, handleSetUpdatePatientData)
  yield takeLatest(
    SET_UPDATE_PATIENT_THRESHOLD,
    handleSetUpdatePatientThresholdData
  )
  yield takeLatest(UPDATE_PATIENT_THRESHOLD, handleUpdatePatientThresholdData)
  yield takeLatest(
    ADD_PATIENT_WITH_PATIENT_KEY,
    handlePatientDataWithPatientKey
  )
}

export const patientSaga = [fork(watcherSaga)]
